import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getStudentReport } from '@/api/student';
import { AiLoading } from '@/components/ui/ai-loading/ai-loading';
import { Card } from '@/components/ui/card/card';
import { ScoreRing } from '@/components/ui/score-ring/score-ring';

import type { DimensionTrend } from '@/types';

import styles from './report.module.css';

function deltaText(delta: number): { text: string; cls: string } {
  if (delta > 0) return { text: `+${Math.round(delta * 100) / 100}`, cls: 'up' };
  if (delta < 0) return { text: `${Math.round(delta * 100) / 100}`, cls: 'down' };
  return { text: '持平', cls: 'flat' };
}

function ratioColor(ratio: number): string {
  if (ratio >= 0.8) return 'good';
  if (ratio >= 0.6) return 'mid';
  return 'weak';
}

function TrendSparkline({ trend }: { trend: DimensionTrend }) {
  // 知识点掌握趋势：按时间展示每次作业的掌握度比例条
  return (
    <div className={styles.sparkRow}>
      <span className={styles.trendName} title={trend.dimension}>
        {trend.dimension}
      </span>
      <div className={styles.sparkPoints}>
        {trend.points.map((p, i) => (
          <div
            key={i}
            className={`${styles.sparkPoint} ${styles[ratioColor(p.ratio)]}`}
            style={{ height: `${Math.max(p.ratio * 100, 6)}%` }}
            title={`${p.job_title}：${Math.round(p.ratio * 100)}%`}
          />
        ))}
      </div>
      <span className={`${styles.sparkLatest} ${styles[ratioColor(trend.latest)]}`}>
        {Math.round(trend.latest * 100)}%
      </span>
      <span className={`${styles.sparkDelta} ${styles[deltaText(trend.delta).cls]}`}>
        {deltaText(trend.delta).text}
      </span>
    </div>
  );
}

export function ReportPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['student-report'],
    queryFn: () => getStudentReport(),
  });

  const maxTrend = data?.score_trend.reduce((m, p) => Math.max(m, p.score), 0) || 100;
  const comparison = data?.comparison;
  const dimensionTrend = data?.dimension_trend ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>学情报告</h1>
        <p className={styles.subtitle}>
          基于你的作业与批阅数据，生成成绩趋势、知识点掌握分析与个性化学习建议。
        </p>
      </header>

      {isLoading && (
        <AiLoading
          title="AI 学情报告生成中"
          hint={['正在汇总作业数据…', '正在分析知识点掌握度…', '正在撰写个性化建议…']}
        />
      )}

      {!isLoading && data && (
        <>
          <div className={styles.overview}>
            <Card className={styles.ringCard}>
              <ScoreRing score={data.avg_score} maxScore={100} />
              <div className={styles.ringLabel}>平均分</div>
            </Card>
            <div className={styles.statGrid}>
              <div className={styles.stat}>
                <div className={styles.statValue}>{data.total_submitted}</div>
                <div className={styles.statLabel}>提交作业</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statValue}>{data.reviewed_count}</div>
                <div className={styles.statLabel}>已批阅</div>
              </div>
              <div className={styles.stat}>
                <div className={styles.statValue}>{data.best_score}</div>
                <div className={styles.statLabel}>最高分</div>
              </div>
            </div>
          </div>

          <div className={styles.grid}>
            <Card className={styles.panel}>
              <h2 className={styles.panelTitle}>成绩趋势</h2>
              {data.score_trend.length === 0 && (
                <div className={styles.empty}>暂无已批阅成绩</div>
              )}
              <div className={styles.trend}>
                {data.score_trend.map((p, i) => (
                  <div key={i} className={styles.trendRow}>
                    <span className={styles.trendName}>{p.job_title}</span>
                    <div className={styles.trendBarWrap}>
                      <div
                        className={styles.trendBar}
                        style={{ width: `${(p.score / maxTrend) * 100}%` }}
                      />
                    </div>
                    <span className={styles.trendScore}>{p.score}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className={styles.panel}>
              <h2 className={styles.panelTitle}>知识点掌握度</h2>
              {data.mastery.length === 0 && (
                <div className={styles.empty}>暂无数据</div>
              )}
              <div className={styles.mastery}>
                {data.mastery.map((m) => (
                  <div key={m.dimension} className={styles.masteryRow}>
                    <span className={styles.masteryName}>{m.dimension}</span>
                    <div className={styles.masteryBarWrap}>
                      <div
                        className={`${styles.masteryBar} ${
                          m.mastery_ratio >= 0.8
                            ? styles.good
                            : m.mastery_ratio >= 0.6
                              ? styles.mid
                              : styles.weak
                        }`}
                        style={{ width: `${m.mastery_ratio * 100}%` }}
                      />
                    </div>
                    <span className={styles.masteryPct}>
                      {Math.round(m.mastery_ratio * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* 前后对比（仅与自己比，不做同学横向对比） */}
          <Card className={styles.panel}>
            <h2 className={styles.panelTitle}>个人前后对比</h2>
            {!comparison || !comparison.available ? (
              <div className={styles.empty}>
                完成两次以上批阅后，这里会展示你的前后成绩对比。
              </div>
            ) : (
              <>
                {comparison.overall && (
                  <div className={styles.compareSummary}>
                    <div className={styles.compareCard}>
                      <div className={styles.compareLabel}>前期平均分</div>
                      <div className={styles.compareValue}>
                        {comparison.overall.early_avg}
                      </div>
                    </div>
                    <div className={styles.compareArrow}>
                      {comparison.overall.delta > 0 ? '→' : comparison.overall.delta < 0 ? '→' : '·'}
                    </div>
                    <div className={styles.compareCard}>
                      <div className={styles.compareLabel}>近期平均分</div>
                      <div className={`${styles.compareValue} ${styles[deltaText(comparison.overall.delta).cls]}`}>
                        {comparison.overall.late_avg}
                      </div>
                    </div>
                    <div className={styles.compareDeltaBox}>
                      <span className={styles.compareDeltaLabel}>变化</span>
                      <span className={`${styles.compareDelta} ${styles[deltaText(comparison.overall.delta).cls]}`}>
                        {deltaText(comparison.overall.delta).text} 分
                      </span>
                      <span className={styles.compareNote}>
                        （前 {comparison.early_count} 次 vs 近 {comparison.late_count} 次的平均）
                      </span>
                    </div>
                  </div>
                )}
                {comparison.dimensions.length > 0 && (
                  <div className={styles.compareDims}>
                    {comparison.dimensions.map((d) => (
                      <div key={d.dimension} className={styles.compareDimRow}>
                        <span className={styles.trendName}>{d.dimension}</span>
                        <div className={styles.compareBarPair}>
                          <div className={styles.compareBarTrack}>
                            <div
                              className={`${styles.compareBar} ${styles[ratioColor(d.early_ratio)]}`}
                              style={{ width: `${d.early_ratio * 100}%` }}
                            />
                          </div>
                          <div className={styles.compareBarTrack}>
                            <div
                              className={`${styles.compareBar} ${styles[ratioColor(d.late_ratio)]}`}
                              style={{ width: `${d.late_ratio * 100}%` }}
                            />
                          </div>
                        </div>
                        <span className={styles.compareDimPct}>
                          {Math.round(d.early_ratio * 100)}% → {Math.round(d.late_ratio * 100)}%
                        </span>
                        <span className={`${styles.sparkDelta} ${styles[deltaText(d.delta).cls]}`}>
                          {deltaText(d.delta).text}
                        </span>
                      </div>
                    ))}
                    <div className={styles.compareLegend}>
                      <span className={styles.legendDotEarly} /> 前期掌握度
                      <span className={styles.legendDotLate} /> 近期掌握度
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* 知识点掌握趋势 */}
          <Card className={styles.panel}>
            <h2 className={styles.panelTitle}>知识点掌握趋势</h2>
            {dimensionTrend.length === 0 ? (
              <div className={styles.empty}>暂无趋势数据</div>
            ) : (
              <div className={styles.sparkList}>
                {dimensionTrend.map((t) => (
                  <TrendSparkline key={t.dimension} trend={t} />
                ))}
              </div>
            )}
          </Card>

          {/* AI 学习建议 */}
          <Card className={styles.panel}>
            <div className={styles.adviceHeader}>
              <h2 className={styles.panelTitle}>AI 学习建议</h2>
              <span className={styles.adviceBadge}>智能生成</span>
            </div>
            {data.ai_advice ? (
              <div className={styles.adviceBody}>{data.ai_advice}</div>
            ) : (
              <div className={styles.empty}>暂无建议</div>
            )}
          </Card>

          <Card className={styles.panel}>
            <h2 className={styles.panelTitle}>最近提交</h2>
            {data.recent.length === 0 && <div className={styles.empty}>暂无提交</div>}
            <div className={styles.recent}>
              {data.recent.map((r) => (
                <div
                  key={r.submission_id}
                  className={styles.recentItem}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/review/${r.submission_id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') navigate(`/review/${r.submission_id}`);
                  }}
                >
                  <span className={styles.recentJob}>{r.job_title}</span>
                  <span className={styles.recentStatus}>
                    {r.reviewed ? `得分 ${r.score}` : '待批阅'}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
